import axios from 'axios';
import { 
  BackgroundProcess, 
  VideoD, 
  PlatformD, 
  VideoViewStatistics, 
  AdD, 
  PublicationStatistic, 
  ParamD, 
  SchedulerConfig,
  ApiResponse,
  PersonD,
  ContractD,
  CreativeD,
  MediaUpload,
  OrdSyncResult,
  OrdClient,
  CreateOrdClientResponse,
  CabinetTariff,
  OrdProvider,
  StartPricingMode,
  ConnectionRequest,
  ConnectionRequestStatus,
  ExistingOrdType,
  LandingPromo,
  CabinetInvoice,
  GrantAccessPermissions,
  DzenReportUploadListItem,
  DzenReportUploadResult,
} from '../types';

export interface DictItem {
  code: string;
  name: string;
}

/** Локально — прямой backend; на проде (build) — относительный /ord/api за Apache. Переопределение: REACT_APP_API_URL */
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:8090/api' : '/ord/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cabinet_auth_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // FormData: сбрасываем application/json из defaults, иначе 415.
  // Браузер сам проставит multipart/form-data; boundary=...
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.set === 'function') {
      config.headers.set('Content-Type', false);
    } else {
      delete (config.headers as any)['Content-Type'];
      delete (config.headers as any)['content-type'];
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const loginPath = `${process.env.PUBLIC_URL || '/ord'}/login`;
    const landingPath = `${process.env.PUBLIC_URL || '/ord'}`;
    const path = window.location.pathname;
    if (error?.response?.status === 401 && !path.startsWith(loginPath) && path !== landingPath && path !== `${landingPath}/`) {
      localStorage.removeItem('cabinet_auth_token');
      window.location.href = loginPath;
    }
    return Promise.reject(error);
  }
);

// Новый тип для последних успешных запусков
export interface LastCompletedProcess {
  code: string;
  description: string;
  process: BackgroundProcess | null;
}

export interface VkordSendRequest {
  startDt: string;
  endDt: string;
  platform: string | null;
  erids?: string[];
  externalVideoIds?: string[];
}

// Универсальный обработчик ответа ApiResponse
export function unwrapApiResponse<T>(promise: Promise<any>): Promise<T> {
  return promise.then(res => {
    if (res.data && typeof res.data === 'object' && 'data' in res.data) {
      return res.data.data;
    }
    return res.data;
  });
}

// Background Processes
export const backgroundProcessApi = {
  getAll: () => unwrapApiResponse<BackgroundProcess[]>(api.get('/background-processes')),
  getLatest: () => unwrapApiResponse<BackgroundProcess[]>(api.get('/background-processes/latest')),
  getByProcessCode: (processCode: string) => 
    unwrapApiResponse<BackgroundProcess[]>(api.get(`/background-processes/process/${processCode}`)),
  getRunning: () => unwrapApiResponse<BackgroundProcess[]>(api.get('/background-processes/running')),
  getByStatus: (statusCode: string) => 
    unwrapApiResponse<BackgroundProcess[]>(api.get(`/background-processes/status/${statusCode}`)),
  collectStats: () => unwrapApiResponse<string>(api.post('/background-processes/collect-stats')),
  getById: (id: number) => unwrapApiResponse<BackgroundProcess>(api.get(`/background-processes/${id}`)),
  getLastCompleted: () => unwrapApiResponse<LastCompletedProcess[]>(api.get('/background-processes/last-completed')),
};

// Scheduler
export const schedulerApi = {
  getConfig: () => unwrapApiResponse<SchedulerConfig>(api.get('/scheduler/config')),
  updateConfig: (config: SchedulerConfig) => 
    unwrapApiResponse<SchedulerConfig>(api.put('/scheduler/config', config)),
  startVkVideoSync: () => unwrapApiResponse<any>(api.post('/scheduler/start/vk-video-sync')),
  startYoutubeSync: () => unwrapApiResponse<any>(api.post('/scheduler/start/youtube-sync')),
  startVkordSync: () => unwrapApiResponse<any>(api.post('/scheduler/start/vkord-send')),
  startStatsCollection: () => unwrapApiResponse<any>(api.post('/scheduler/start/stats-collection')),
  startVkordSend: (data: VkordSendRequest) =>
    unwrapApiResponse(api.post('/scheduler/start/vkord-send', data)),
  startVkStatsCollection: () => unwrapApiResponse<any>(api.post('/scheduler/start/vk-stats-collection')),
  startYouTubeStatsCollection: () => unwrapApiResponse<any>(api.post('/scheduler/start/youtube-stats-collection')),
  startRutubeSync: () => unwrapApiResponse<any>(api.post('/scheduler/start/rutube-sync')),
  startRutubeStatsCollection: () => unwrapApiResponse<any>(api.post('/scheduler/start/rutube-stats-collection')),
  startCabinetMonthlyReport: (params?: { startDt?: string; endDt?: string }) =>
    unwrapApiResponse<any>(api.post('/scheduler/start/cabinet-monthly-report', null, { params })),
};

// Videos
export const videoApi = {
  getAll: () => unwrapApiResponse<VideoD[]>(api.get('/videos')),
  getById: (id: number) => unwrapApiResponse<VideoD>(api.get(`/videos/${id}`)),
  getByPlatform: (platformId: number) => 
    unwrapApiResponse<VideoD[]>(api.get(`/videos/platform/${platformId}`)),
  getByProcess: (processId: number) => unwrapApiResponse<VideoD[]>(api.get(`/videos/process/${processId}`)),
};

/** Очистка собранных видео/рекламы/статистики (кабинет ОРД не трогает). */
export const dataPipelineApi = {
  clearCollected: () =>
    unwrapApiResponse<Record<string, number>>(api.post('/data-pipeline/clear-collected')),
};

export type MonitorChannel = {
  id: number;
  platform: 'YOUTUBE' | 'VK_VIDEO' | string;
  channelId: string;
  name: string;
  platformExternalId: string;
  enabled: boolean;
  source: string;
  personDId?: number | null;
  personName?: string | null;
};

export const monitorChannelApi = {
  list: () => unwrapApiResponse<MonitorChannel[]>(api.get('/monitor-channels')),
  create: (data: {
    platform: string;
    channelId: string;
    name?: string;
    platformExternalId?: string;
    enabled?: boolean;
  }) => unwrapApiResponse<MonitorChannel>(api.post('/monitor-channels', data)),
  update: (id: number, data: Partial<{
    platform: string;
    channelId: string;
    name: string;
    platformExternalId: string;
    enabled: boolean;
  }>) => unwrapApiResponse<MonitorChannel>(api.put(`/monitor-channels/${id}`, data)),
  remove: (id: number) => unwrapApiResponse<void>(api.delete(`/monitor-channels/${id}`)),
  sync: (id: number) =>
    unwrapApiResponse<{ channelId: string; name: string; platform: string }>(
      api.post(`/monitor-channels/${id}/sync`, {}, { timeout: 30 * 60 * 1000 })
    ),
};
// Platforms
export const platformApi = {
  getAll: () => unwrapApiResponse<PlatformD[]>(api.get('/platforms')),
  getById: (id: number) => unwrapApiResponse<PlatformD>(api.get(`/platforms/${id}`)),
  getByProcess: (processId: number) => unwrapApiResponse<PlatformD[]>(api.get(`/platforms/process/${processId}`)),
  create: (platform: Omit<PlatformD, 'platformDId' | 'createdTs' | 'changedTs' | 'isDeleted'>) => 
    unwrapApiResponse<PlatformD>(api.post('/platforms', platform)),
  update: (id: number, platform: Partial<PlatformD>) => 
    unwrapApiResponse<PlatformD>(api.put(`/platforms/${id}`, platform)),
  delete: (id: number) => unwrapApiResponse<any>(api.delete(`/platforms/${id}`)),
};

// Video View Statistics
export const videoViewStatisticsApi = {
  getAll: () => unwrapApiResponse<VideoViewStatistics[]>(api.get('/video-view-statistics')),
  getByVideoId: (videoId: number) => 
    unwrapApiResponse<VideoViewStatistics[]>(api.get(`/video-view-statistics/video/${videoId}`)),
  getLatest: () => unwrapApiResponse<VideoViewStatistics[]>(api.get('/video-view-statistics/latest')),
  getByProcess: (processId: number) => unwrapApiResponse<VideoViewStatistics[]>(api.get(`/video-view-statistics/process/${processId}`)),
  update: (statisticsId: number, data: { views: number; collectedTs: string }) => 
    unwrapApiResponse<VideoViewStatistics>(api.put(`/video-view-statistics/${statisticsId}`, data)),
};

// Ads
export const adApi = {
  getAll: () => unwrapApiResponse<AdD[]>(api.get('/ads')),
  getById: (id: number) => unwrapApiResponse<AdD>(api.get(`/ads/${id}`)),
  getByPlatform: (platformId: number) => 
    unwrapApiResponse<AdD[]>(api.get(`/ads/platform/${platformId}`)),
  getByProcess: (processId: number) => unwrapApiResponse<AdD[]>(api.get(`/ads/process/${processId}`)),
};

// Publication Statistics
export const publicationStatisticApi = {
  getAll: () => unwrapApiResponse<PublicationStatistic[]>(api.get('/publication-statistics')),
  getByVideoId: (videoId: number) => 
    unwrapApiResponse<PublicationStatistic[]>(api.get(`/publication-statistics/video/${videoId}`)),
  getLatest: () => unwrapApiResponse<PublicationStatistic[]>(api.get('/publication-statistics/latest')),
  getByProcess: (processId: number) => unwrapApiResponse<PublicationStatistic[]>(api.get(`/publication-statistics/process/${processId}`)),
};

// Parameters
export const paramApi = {
  getAll: () => unwrapApiResponse<ParamD[]>(api.get('/params')),
  getByCode: (code: string) => unwrapApiResponse<ParamD>(api.get(`/params/code/${code}`)),
  update: (id: number, param: Partial<ParamD>) => 
    unwrapApiResponse<ParamD>(api.put(`/params/${id}`, param)),
};

export const videoAdApi = {
  getAll: (params: any) => unwrapApiResponse<any>(api.get('/video-ads', { params })),
  updateIsActive: (id: number, isActive: boolean) => unwrapApiResponse<any>(api.patch(`/video-ads/${id}/is-active`, null, { params: { isActive } })),
};

export const ordVkApi = {
  getCounterparties: () => unwrapApiResponse<any[]>(api.get('/ordvk/counterparties')),
  getCreatives: () => unwrapApiResponse<any[]>(api.get('/ordvk/creatives')),
  getStatistics: () => unwrapApiResponse<any[]>(api.get('/ordvk/statistics')),
};

export const cabinetPersonApi = {
  list: () => unwrapApiResponse<PersonD[]>(api.get('/cabinet/persons')),
  create: (data: Partial<PersonD> & { name: string; syncToOrd?: boolean }) =>
    unwrapApiResponse<PersonD>(api.post('/cabinet/persons', data)),
  sync: (id: number) => unwrapApiResponse<PersonD>(api.post(`/cabinet/persons/${id}/sync`)),
  updateAdLimits: (id: number, data: { weeklyAdLimit?: number | null; monthlyAdLimit?: number | null }) =>
    unwrapApiResponse<PersonD>(api.put(`/cabinet/persons/${id}/ad-limits`, data)),
  listChannels: (personId: number) =>
    unwrapApiResponse<MonitorChannel[]>(api.get(`/cabinet/persons/${personId}/channels`)),
  addChannel: (personId: number, data: {
    platform: string;
    channelId: string;
    name?: string;
    enabled?: boolean;
  }) => unwrapApiResponse<MonitorChannel>(api.post(`/cabinet/persons/${personId}/channels`, data)),
  removeChannel: (personId: number, channelId: number) =>
    unwrapApiResponse<void>(api.delete(`/cabinet/persons/${personId}/channels/${channelId}`)),
  syncChannels: (personId: number) =>
    unwrapApiResponse<{
      channelsProcessed: number;
      scanned: number;
      matched: number;
      stillAwaiting: number;
      linkedYoutube: number;
      linkedVk: number;
      linkedRutube?: number;
      linkedTelegram?: number;
    }>(api.post(`/cabinet/persons/${personId}/sync-channels`, {}, { timeout: 30 * 60 * 1000 })),
};

export const cabinetContractApi = {
  list: () => unwrapApiResponse<ContractD[]>(api.get('/cabinet/contracts')),
  create: (data: any) => unwrapApiResponse<ContractD>(api.post('/cabinet/contracts', data)),
  sync: (id: number) => unwrapApiResponse<ContractD>(api.post(`/cabinet/contracts/${id}/sync`)),
};

export const cabinetCreativeApi = {
  list: () => unwrapApiResponse<CreativeD[]>(api.get('/cabinet/creatives')),
  awaitingLinks: () => unwrapApiResponse<CreativeD[]>(api.get('/cabinet/creatives/awaiting-links')),
  get: (id: number) => unwrapApiResponse<CreativeD>(api.get(`/cabinet/creatives/${id}`)),
  create: (data: any) => unwrapApiResponse<CreativeD>(api.post('/cabinet/creatives', data)),
  update: (id: number, data: any) => unwrapApiResponse<CreativeD>(api.put(`/cabinet/creatives/${id}`, data)),
  delete: (id: number) => unwrapApiResponse<void>(api.delete(`/cabinet/creatives/${id}`)),
  sendToOrd: (id: number) => unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/send-to-ord`)),
  attachLinks: (id: number, data: {
    youtubeUrl?: string;
    vkUrl?: string;
    rutubeUrl?: string;
    dzenUrl?: string;
    telegramUrl?: string;
    youtubePlatformDId?: number;
    vkPlatformDId?: number;
    rutubePlatformDId?: number;
    dzenPlatformDId?: number;
    telegramPlatformDId?: number;
  }) =>
    unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/links`, data)),
  importFromOrd: () =>
    unwrapApiResponse<OrdSyncResult>(
      api.post('/cabinet/creatives/import-from-ord', {}, { timeout: 30 * 60 * 1000 })
    ),
  enrichOrdDates: () =>
    unwrapApiResponse<number>(
      api.post('/cabinet/creatives/enrich-ord-dates', {}, { timeout: 30 * 60 * 1000 })
    ),
  autoLinkFromCollected: () =>
    unwrapApiResponse<{
      channelsProcessed: number;
      scanned: number;
      matched: number;
      stillAwaiting: number;
      linkedYoutube: number;
      linkedVk: number;
      linkedRutube?: number;
      linkedTelegram?: number;
      datesEnriched?: number;
    }>(api.post('/cabinet/creatives/auto-link-from-collected', {}, { timeout: 30 * 60 * 1000 })),
  skipStats: (id: number) => unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/skip-stats`)),
  restoreStats: (id: number) => unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/restore-stats`)),
  listMissingLinks: () => unwrapApiResponse<CreativeD[]>(api.get('/cabinet/creatives/missing-links')),
  acceptAllMissingLinks: () => unwrapApiResponse<number>(api.post('/cabinet/creatives/missing-links/accept-all')),
  recheckLinks: (id: number) => unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/recheck-links`)),
  copy: (id: number) => unwrapApiResponse<CreativeD>(api.post(`/cabinet/creatives/${id}/copy`)),
  collectViews: (id: number) => unwrapApiResponse<number>(api.post(`/cabinet/creatives/${id}/collect-views`)),
  submitInvoice: (id: number) => unwrapApiResponse<void>(api.post(`/cabinet/creatives/${id}/submit-invoice`)),
  submitStats: (id: number) => unwrapApiResponse<void>(api.post(`/cabinet/creatives/${id}/submit-stats`)),
};

export const cabinetMediaApi = {
  list: () => unwrapApiResponse<MediaUpload[]>(api.get('/cabinet/media')),
  upload: (file: File, onUploadProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return unwrapApiResponse<MediaUpload>(
      api.post('/cabinet/media', form, {
        // Не задаём Content-Type вручную — браузер сам добавит boundary
        timeout: 15 * 60 * 1000,
        onUploadProgress: (event) => {
          if (!onUploadProgress || !event.total) return;
          onUploadProgress(Math.round((event.loaded * 100) / event.total));
        },
      })
    );
  },
};

export const cabinetDictApi = {
  kktu: (search?: string) =>
    unwrapApiResponse<DictItem[]>(api.get('/cabinet/dict/kktu', { params: { search, limit: 40 } })),
  forms: () => unwrapApiResponse<DictItem[]>(api.get('/cabinet/dict/forms')),
  payTypes: () => unwrapApiResponse<DictItem[]>(api.get('/cabinet/dict/pay-types')),
  padTypes: () => unwrapApiResponse<DictItem[]>(api.get('/cabinet/dict/pad-types')),
  countries: () => unwrapApiResponse<DictItem[]>(api.get('/cabinet/dict/countries')),
};

export const cabinetPlatformApi = {
  list: () => unwrapApiResponse<PlatformD[]>(api.get('/cabinet/platforms')),
  create: (data: {
    name: string;
    padType: string;
    url: string;
    personDId: number;
    isOwner?: boolean;
    hostingCd?: string;
    syncToOrd?: boolean;
  }) => unwrapApiResponse<PlatformD>(api.post('/cabinet/platforms', data)),
  sync: (id: number) => unwrapApiResponse<PlatformD>(api.post(`/cabinet/platforms/${id}/sync`)),
  importFromOrd: () => unwrapApiResponse<{ imported: number }>(api.post('/cabinet/platforms/import-from-ord')),
  remove: (id: number) => unwrapApiResponse<void>(api.delete(`/cabinet/platforms/${id}`)),
};

export const settingsApi = {
  getTestMode: () => unwrapApiResponse<{ enabled: boolean }>(api.get('/settings/test-mode')),
  setTestMode: (enabled: boolean) =>
    unwrapApiResponse<{ enabled: boolean }>(api.put('/settings/test-mode', { enabled })),
  getAutoMode: () => unwrapApiResponse<{ enabled: boolean }>(api.get('/settings/auto-mode')),
  setAutoMode: (enabled: boolean) =>
    unwrapApiResponse<{ enabled: boolean }>(api.put('/settings/auto-mode', { enabled })),
  getLandingPromo: () => unwrapApiResponse<LandingPromo>(api.get('/settings/landing-promo')),
  setLandingPromo: (payload: LandingPromo) =>
    unwrapApiResponse<LandingPromo>(api.put('/settings/landing-promo', payload)),
};

export const publicLandingApi = {
  getPromo: () => unwrapApiResponse<LandingPromo>(api.get('/public/landing-promo')),
};

export const authApi = {
  login: (login: string, password: string) =>
    unwrapApiResponse<{ token: string; user: any }>(api.post('/auth/login', { login, password })),
  logout: () => unwrapApiResponse<void>(api.post('/auth/logout')),
  me: () => unwrapApiResponse<any>(api.get('/auth/me')),
  listAccess: () => unwrapApiResponse<any[]>(api.get('/auth/access')),
  grantAccess: (personId: number, permissions?: GrantAccessPermissions) =>
    unwrapApiResponse<{ userId: number; login: string; password: string; inn: string; personName: string }>(
      api.post(`/auth/access/person/${personId}`, permissions || { accessCreatives: true })
    ),
  grantSelfAdAccess: (personName?: string, permissions?: GrantAccessPermissions) =>
    unwrapApiResponse<{ userId: number; login: string; password: string; inn: string; personName: string }>(
      api.post('/auth/access/self-ad', { personName, ...(permissions || { accessCreatives: true }) })
    ),
  revokeAccess: (userId: number) => unwrapApiResponse<void>(api.delete(`/auth/access/${userId}`)),
  changeCredentials: (payload: { currentPassword: string; newLogin?: string; newPassword?: string }) =>
    unwrapApiResponse<void>(api.post('/auth/change-credentials', payload)),
};

export const ordClientsApi = {
  list: () => unwrapApiResponse<OrdClient[]>(api.get('/cabinet/clients')),
  get: (id: number) => unwrapApiResponse<OrdClient>(api.get(`/cabinet/clients/${id}`)),
  create: (payload: {
    name: string;
    inn?: string;
    contactName?: string;
    tariff: CabinetTariff;
    ordProvider?: OrdProvider;
    ordToken?: string;
    subscriptionPaidUntil?: string;
    startPricingMode?: StartPricingMode;
    startPricingQuantity?: number;
    autoReportEnabled?: boolean;
    login?: string;
    allowSelfAd?: boolean;
    statsSubmissionEnabled?: boolean;
  }) => unwrapApiResponse<CreateOrdClientResponse>(api.post('/cabinet/clients', payload)),
  update: (id: number, payload: Partial<{
    name: string;
    inn: string;
    contactName: string;
    tariff: CabinetTariff;
    ordProvider: OrdProvider;
    ordToken: string;
    subscriptionPaidUntil: string;
    startPricingMode: StartPricingMode;
    startPricingQuantity: number;
    autoReportEnabled: boolean;
    statsSubmissionEnabled: boolean;
    enabled: boolean;
  }>) => unwrapApiResponse<OrdClient>(api.put(`/cabinet/clients/${id}`, payload)),
  resetPassword: (id: number) =>
    unwrapApiResponse<CreateOrdClientResponse>(api.post(`/cabinet/clients/${id}/reset-password`)),
  renew: (id: number, payload: {
    subscriptionPaidUntil?: string;
    months?: number;
    tariff?: CabinetTariff;
    startPricingMode?: StartPricingMode;
    startPricingQuantity?: number;
    statsSubmissionEnabled?: boolean;
  }) => unwrapApiResponse<OrdClient>(api.post(`/cabinet/clients/${id}/renew`, payload)),
  delete: (id: number) => unwrapApiResponse<void>(api.delete(`/cabinet/clients/${id}`)),
};

export const cabinetInvoiceApi = {
  list: () => unwrapApiResponse<CabinetInvoice[]>(api.get('/cabinet/invoices')),
  syncFromOrd: () =>
    unwrapApiResponse<number>(
      api.post('/cabinet/invoices/sync-from-ord', {}, { timeout: 10 * 60 * 1000 })
    ),
  submit: (creativeId: number) => unwrapApiResponse<void>(api.post(`/cabinet/invoices/${creativeId}/submit`)),
};

export const cabinetSelfAdChannelApi = {
  list: () => unwrapApiResponse<MonitorChannel[]>(api.get('/cabinet/self-ad/channels')),
  add: (data: {
    platform: string;
    channelId: string;
    name?: string;
    enabled?: boolean;
  }) => unwrapApiResponse<MonitorChannel>(api.post('/cabinet/self-ad/channels', data)),
  remove: (channelId: number) =>
    unwrapApiResponse<void>(api.delete(`/cabinet/self-ad/channels/${channelId}`)),
  sync: () =>
    unwrapApiResponse<{
      channelsProcessed: number;
      scanned: number;
      matched: number;
      stillAwaiting: number;
      linkedYoutube: number;
      linkedVk: number;
      linkedRutube?: number;
      linkedTelegram?: number;
    }>(api.post('/cabinet/self-ad/channels/sync', {}, { timeout: 30 * 60 * 1000 })),
};

export const cabinetUsersApi = {
  list: () => unwrapApiResponse<any[]>(api.get('/cabinet/users')),
  create: (payload: {
    displayName: string;
    accessCreatives?: boolean;
    accessPersons?: boolean;
    accessContracts?: boolean;
    accessInvoices?: boolean;
  }) => unwrapApiResponse<{ userId: number; login: string; password: string; personName: string }>(
    api.post('/cabinet/users', payload)
  ),
  revoke: (userId: number) => unwrapApiResponse<void>(api.delete(`/cabinet/users/${userId}`)),
};

export const cabinetSettingsApi = {
  get: () => unwrapApiResponse<OrdClient | null>(api.get('/cabinet/settings')),
  update: (payload: {
    ordProvider?: OrdProvider;
    ordToken?: string;
    autoReportEnabled?: boolean;
    testModeEnabled?: boolean;
    dzenSubmissionEnabled?: boolean;
  }) => unwrapApiResponse<OrdClient>(api.put('/cabinet/settings', payload)),
};

export const dzenReportApi = {
  list: () => unwrapApiResponse<DzenReportUploadListItem[]>(api.get('/cabinet/dzen/reports')),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrapApiResponse<DzenReportUploadResult>(api.post('/cabinet/dzen/reports', form));
  },
};

export const yandexSubmittedViewsApi = {
  importSubmittedViews: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrapApiResponse<{
      filename: string;
      matchedCount: number;
      savedCount: number;
      unmatchedCount: number;
      skippedCount: number;
      rows: Array<{
        erid?: string;
        viewsInFile?: number;
        creativeDId?: number;
        creativeName?: string;
        status?: string;
        message?: string;
      }>;
    }>(api.post('/cabinet/yandex/submitted-views', form));
  },
};

export const connectionRequestsApi = {
  create: (payload: {
    contactName?: string;
    email?: string;
    messenger?: string;
    creativesPerMonth: number;
    existingOrd?: ExistingOrdType;
    comment?: string;
  }) => unwrapApiResponse<ConnectionRequest>(api.post('/public/connection-requests', payload)),
  list: () => unwrapApiResponse<ConnectionRequest[]>(api.get('/cabinet/connection-requests')),
  update: (id: number, payload: { status?: ConnectionRequestStatus; adminNote?: string }) =>
    unwrapApiResponse<ConnectionRequest>(api.put(`/cabinet/connection-requests/${id}`, payload)),
};

export default api;
